import { useMemo, useState } from 'react';
import {
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material';
import { Section, SectionCard, SectionHeader } from '@/components/common';
import { useAppraisalAuditHistory } from '@/queries/useAppraisalDetail';
import type { AppraisalAuditRecord } from '@/api/types/audit.types';
import { formatDateTimeWithSeconds as formatDateTime } from '@/utils/dateFormatter';

type SortDirection = 'asc' | 'desc';

interface AuditHistorySectionProps {
  appraisalId: string;
}

export function AuditHistorySection({ appraisalId }: AuditHistorySectionProps) {
  const [sortDir, setSortDir] = useState<SortDirection>('desc');
  const { data: auditRecords, isLoading } = useAppraisalAuditHistory(appraisalId);

  const sortedRecords = useMemo(() => {
    if (!auditRecords) return [];
    return [...auditRecords].sort((a, b) => {
      const dateA = new Date(a.occurredAt).getTime();
      const dateB = new Date(b.occurredAt).getTime();
      return sortDir === 'asc' ? dateA - dateB : dateB - dateA;
    });
  }, [auditRecords, sortDir]);

  return (
    <Section>
      <SectionHeader>AUDIT HISTORY</SectionHeader>
      <SectionCard>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : sortedRecords.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
            No audit history
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>
                    <TableSortLabel
                      active
                      direction={sortDir}
                      onClick={() => setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))}
                    >
                      DATE/TIME
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>ACTIVITY</TableCell>
                  <TableCell>USER</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedRecords.map((record: AppraisalAuditRecord, index: number) => (
                  <TableRow key={`${record.occurredAt}-${index}`}>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {formatDateTime(record.occurredAt)}
                    </TableCell>
                    <TableCell>{record.description}</TableCell>
                    <TableCell>{record.performedBy}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </SectionCard>
    </Section>
  );
}

export default AuditHistorySection;
